import React, { useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    CircularProgress,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { ITefConfig } from '../../../../../../types/api/financeiro/pesquisar-tef-configs';
import { IDataFormasRecebimento } from '../../../../../../types/api/financeiro/pesquisar-formas-recebimento';
import { ICriarTefConfig, IEditarTefConfig } from '../../../../../../types/mutations/financeiro/crud-tef-config';
import axiosClient from '../../../../../../lib/axios';
import PaperDraggable from '../../../../../../components/PaperDraggable';
import SelectFormaRecebimentoMultiple from '../../../../../../components/SelectsSimpleApiQuery/SelectFormaRecebimento/Multiple';
import SelectLoja from '../../../../../../components/SelectsSimpleApiQuery/SelectLoja';
import { IDataLojas } from '../../../../../../types/api/geral/pesquisar-lojas';

interface DialogCriacaoEditConfigProps {
    open: boolean;
    handleClose: () => void;
    defaultValue?: ITefConfig | null;
}

interface FormData {
    nome: string;
    cod_ativacao: string;
    autorizacao: string;
    loja: IDataLojas | null;
    formas_recebimento: IDataFormasRecebimento[] | null;
}

export default function DialogCriacaoEditConfig({
    open,
    handleClose,
    defaultValue
}: DialogCriacaoEditConfigProps) {
    const queryClient = useQueryClient();
    const isEdit = !!defaultValue;

    const methods = useForm<FormData>({
        defaultValues: {
            nome: '',
            cod_ativacao: '',
            autorizacao: '',
            loja: null,
            formas_recebimento: null
        }
    });

    const { register, handleSubmit, reset, setValue, formState: { errors } } = methods;

    // Mutation para criar configuração TEF
    const criarTefConfigMutation = useMutation({
        mutationFn: (data: FormData): Promise<ICriarTefConfig> => {
            const payload = {
                nome: data.nome,
                loja_id: data.loja?.id,
                cod_ativacao: data.cod_ativacao,
                autorizacao: data.autorizacao,
                formas_recebimento_ids: data.formas_recebimento?.map(fr => fr.id) || []
            };
            return axiosClient.post('/financeiro/configuracoes/cadastrar-tef-config', payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pesquisar-tef-configs'] });
            handleCloseDialog();
        }
    });

    // Mutation para editar configuração TEF
    const editarTefConfigMutation = useMutation({
        mutationFn: (data: FormData): Promise<IEditarTefConfig> => {
            const payload = {
                id: defaultValue!.id,
                nome: data.nome,
                cod_ativacao: data.cod_ativacao,
                autorizacao: data.autorizacao,
                formas_recebimento_ids: data.formas_recebimento?.map(fr => fr.id) || []
            };
            return axiosClient.put(`/financeiro/configuracoes/atualizar-tef-config/${defaultValue!.id}`, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pesquisar-tef-configs'] });
            handleCloseDialog();
        }
    });

    // Carregar dados para edição
    useEffect(() => {
        if (defaultValue && open) {
            setValue('nome', defaultValue.nome);
            setValue('loja', defaultValue.loja);
            setValue('cod_ativacao', defaultValue.cod_ativacao);
            setValue('autorizacao', defaultValue.autorizacao);
            
            // Converter formas de recebimento para o formato esperado
            if (defaultValue.formas_recebimento) {
                const formasRecebimento = defaultValue.formas_recebimento
                    .filter(fr => fr.id !== null && fr.nome !== null)
                    .map(fr => ({
                        id: fr.id!,
                        nome: fr.nome!,
                        status: fr.status!,
                        flg_cartao_credito: (fr.flg_cartao_credito || 'N') as 'S' | 'N',
                        flg_usa_receber: (fr.flg_usa_receber || 'N') as 'S' | 'N',
                        flg_usa_pagar: (fr.flg_usa_pagar || 'N') as 'S' | 'N',
                        flg_cheque: 'N' as 'S' | 'N',
                        flg_usa_pix: 'N' as 'S' | 'N'
                    }));
                setValue('formas_recebimento', formasRecebimento);
            }
        }
    }, [defaultValue, open, setValue]);

    const handleCloseDialog = () => {
        reset();
        handleClose();
    };

    const onSubmit = (data: FormData) => {
        if (isEdit) {
            editarTefConfigMutation.mutate(data);
        } else {
            criarTefConfigMutation.mutate(data);
        }
    };

    const isLoading = criarTefConfigMutation.isLoading || editarTefConfigMutation.isLoading;

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    {isEdit ? 'Editar Configuração TEF' : 'Criar Configuração TEF'}
                </DialogTitle>
                
                <DialogContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{xs: 12, md: 6}}>
                                <TextField
                                    label="Nome da configuração TEF"
                                    fullWidth
                                    autoFocus
                                    {...register('nome', { 
                                        required: 'O nome da configuração é obrigatório' 
                                    })}
                                    error={!!errors.nome}
                                    helperText={errors.nome?.message}
                                    disabled={isLoading}
                                />
                            </Grid>

                            <Grid size={{xs: 12, md: 6}}>
                                <TextField
                                    label="Código de ativação"
                                    fullWidth
                                    {...register('cod_ativacao', { 
                                        required: 'O código de ativação é obrigatório' 
                                    })}
                                    error={!!errors.cod_ativacao}
                                    helperText={errors.cod_ativacao?.message}
                                    disabled={isLoading}
                                />
                            </Grid>

                            <Grid size={{xs: 12, md: 6}}>
                                <TextField
                                    label="Autorização"
                                    fullWidth
                                    {...register('autorizacao', { 
                                        required: 'A autorização é obrigatória' 
                                    })}
                                    error={!!errors.autorizacao}
                                    helperText={errors.autorizacao?.message}
                                    disabled={isLoading}
                                />
                            </Grid>

                            <Grid size={{xs: 12, md: 6}}>
                                <SelectLoja
                                    value={methods.watch('loja')}
                                    onChange={(value) => {
                                        setValue('loja', value);
                                        setValue('formas_recebimento', null);
                                    }}
                                    required={true}
                                    error={!!errors.loja}
                                    label="Selecione uma loja"
                                />
                            </Grid>

                            <Grid size={{xs: 12}}>
                                <SelectFormaRecebimentoMultiple
                                    value={methods.watch('formas_recebimento')}
                                    loja_id={methods.watch('loja')?.id ?? 0}
                                    onChange={(value) => setValue('formas_recebimento', value)}
                                    error={!!errors.formas_recebimento}
                                    required={false}
                                    isDisabled={!methods.watch('loja')}
                                />
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={handleCloseDialog}
                        disabled={isLoading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading}
                    >
                        {isLoading && (
                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                        )}
                        {isEdit ? 'Atualizar' : 'Criar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    );
}